import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";

// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Popper from "@material-ui/core/Popper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

// @material-ui/icons
import ArrowBack from "@material-ui/icons/ArrowBack";
import Save from "@material-ui/icons/Save";
import Search from "@material-ui/icons/Search";
import CloudUpload from "@material-ui/icons/CloudUpload";
import Delete from "@material-ui/icons/Delete";
import Edit from "@material-ui/icons/Edit";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

// category API functions
import { getCategories, updateCategory, createCategory, getTaxCodes, uploadCategoryImage } from "../../api";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const REDEMPTION_EXEMPT_STATES = ["CA", "CT", "HI", "IA", "MA", "ME", "MI", "NY", "OR", "VT"];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC"
];

const DISCOUNT_TYPES = [
  { value: "discount_override", label: "Discount Override", description: "Set all items to a specific price" },
  { value: "discount_modifier", label: "Discount Amount", description: "Subtract fixed amount from price" },
  { value: "discount_percent", label: "Discount Percent", description: "Apply percentage discount" },
];

function CategoryEditPage(props) {
  const { classes, user } = props;
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ icon: false, image: false });
  const [categories, setCategories] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  
  // Parent category search state
  const [parentSearch, setParentSearch] = useState('');

  // Tax code search state
  const [taxCodeSearch, setTaxCodeSearch] = useState('');
  const [taxCodeDropdownOpen, setTaxCodeDropdownOpen] = useState(false);
  const [taxCodeAnchorEl, setTaxCodeAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Details
    name: "",
    subtitle: "",
    html: "",
    url_name: "",
    url_prefix: false,
    enabled: true,
    parent_id: null,
    
    // Platforms
    platforms: [],
    
    // Navigation
    display_subproducts: true,
    display_subcategories: false,
    
    // Mobile Display
    showTop: false,
    showMainMenu: false,
    
    // Web Storefront
    showStorefront: false,
    web_nav_enabled: false,
    web_display: "",
    web_display_name: "",
    web_ordinal: 0,
    
    // Images
    icon: "",
    image: "",
    
    // Position & Tags
    ordinal: 0,
    tag: "",
    
    // Seller Specific
    sellers: [],

    // Display
    root_category_display: [],
    // Tax
    avatax_code: "P000000",
    redemption_exempt_states: {},
    redemption_inherited: true,
    
    // SEO
    seo_title: "",
    seo_keywords: "",
    seo_description: "",
    search_tags: "",
    seo_brand_title: "",
    google_product: "",
    
    // Category Discounts
    discount_value: 0,
    discount_type: "discount_modifier",
    interval_start: "",
    interval_end: "",
    banner_title: "",
    banner_subtitle: "",
    limit_quantity: 0,
    
    // Footer
    footer: "",
    
    // Dynamic Products
    dynamic: false,
    min_sell_price: 0,
    max_sell_price: 0,
    package: false,
    dynamic_reference_id: null,
    
    // Age Restrictions
    is_alcohol: false,
    is_tobacco: false,

    // Geo Restrictions
    mp_georestriction: [],
  });

  const [originalAgeRestriction, setOriginalAgeRestriction] = useState({ is_alcohol: false, is_tobacco: false });
  const [ageRestrictionConfirmOpen, setAgeRestrictionConfirmOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Check if categories were passed via router state
      const passedCategories = location.state?.categories;
      const passedSelectedCategory = location.state?.selectedCategory;
      
      let categoryData;
      if (passedCategories) {
        // Use passed categories data (no API call needed)
        console.log('Using passed categories data, skipping API call');
        categoryData = passedCategories;
      } else {
        // Fallback: fetch categories from API
        console.log('No categories passed, fetching from API');
        const response = await getCategories();
        categoryData = response.data;
      }
      
      setCategories(categoryData);
      
      if (isEdit) {
        // If we have the selected category passed, use it directly
        let category = passedSelectedCategory;
        
        // Otherwise, find it in the categories data
        if (!category) {
          category = findCategoryById(categoryData, id);
        }
        
        if (category) {
          const taxCodeResponse = await getTaxCodes(category.is_alcohol);
          setTaxCodes(taxCodeResponse.data);
          setOriginalAgeRestriction({
            is_alcohol: Boolean(category.is_alcohol),
            is_tobacco: Boolean(category.is_tobacco),
          });
          setFormData({
            ...formData,
            ...category,
            // Convert platforms array to individual checkboxes for compatibility
            platforms: category.platforms || [],
            // Convert root_category_display array to object
            root_category_display: category.root_category_display || [],
            // Convert redemption_exempt_states array to object
            redemption_exempt_states: convertArrayToStateObject(category.redemption_exempt_states || []),
          });
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const findCategoryById = (categories, id) => {
    for (const category of categories) {
      if (category._id === id) return category;
      if (category.children) {
        const found = findCategoryById(category.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const convertArrayToStateObject = (statesArray) => {
    const stateObj = {};
    REDEMPTION_EXEMPT_STATES.forEach(state => {
      stateObj[state] = statesArray.includes(state);
    });
    return stateObj;
  };

  const convertStateObjectToArray = (stateObj) => {
    return Object.keys(stateObj).filter(state => stateObj[state]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const removeFormField = (field) => {

    setFormData(prev => {
      const newFormData = { ...prev };
      newFormData[field] = null;
      return newFormData;
    });
  };

  const handlePlatformToggle = (platform) => {
    const currentPlatforms = formData.platforms || [];
    const updatedPlatforms = currentPlatforms.includes(platform)
      ? currentPlatforms.filter(p => p !== platform)
      : [...currentPlatforms, platform];
    
    handleInputChange('platforms', updatedPlatforms);
  };
  const handleRootCategoryDisplayToggle = (display) => {
    const currentRootCategoryDisplay = formData.root_category_display || [];
    const updatedRootCategoryDisplay = currentRootCategoryDisplay.includes(display)
      ? currentRootCategoryDisplay.filter(d => d !== display)
      : [...currentRootCategoryDisplay, display];
    
    handleInputChange('root_category_display', updatedRootCategoryDisplay);
  };

  const handleParentChange = (category) => {
    const isDeselecting = formData.parent_id === category._id;
    if (isDeselecting) {
      handleInputChange('parent_id', null);
    } else {
      setFormData(prev => ({
        ...prev,
        parent_id: category._id,
        is_alcohol: Boolean(category.is_alcohol),
        is_tobacco: Boolean(category.is_tobacco),
      }));
    }
  };

  const handleRedemptionStateToggle = (state) => {
    handleInputChange('redemption_exempt_states', {
      ...formData.redemption_exempt_states,
      [state]: !formData.redemption_exempt_states[state]
    });
  };

  // Geo restriction handlers
  const handleGeoRestrictionAdd = (state) => {
    if (!formData.mp_georestriction.includes(state)) {
      handleInputChange('mp_georestriction', [...formData.mp_georestriction, state]);
    }
  };

  const handleGeoRestrictionRemove = (state) => {
    handleInputChange('mp_georestriction', formData.mp_georestriction.filter(s => s !== state));
  };

  const getAvailableStatesForGeoRestriction = () => {
    return US_STATES.filter(state => !formData.mp_georestriction.includes(state));
  };

  const handleBack = () => {
    console.log('Back button clicked, navigating to /categories');
    history.push('/categories');
  };

  const ageRestrictionChanged = () => {
    return (
      Boolean(formData.is_alcohol) !== originalAgeRestriction.is_alcohol ||
      Boolean(formData.is_tobacco) !== originalAgeRestriction.is_tobacco
    );
  };

  const bothAgeRestrictionsOff = () => !formData.is_alcohol && !formData.is_tobacco;

  const handleSave = () => {
    if (isEdit && (ageRestrictionChanged() || bothAgeRestrictionsOff())) {
      setAgeRestrictionConfirmOpen(true);
    } else {
      handleSaveConfirmed();
    }
  };

  const handleSaveConfirmed = async () => {
    setAgeRestrictionConfirmOpen(false);
    try {
      setSaving(true);
      const saveData = {
        ...formData,
        redemption_exempt_states: convertStateObjectToArray(formData.redemption_exempt_states),
      };
      
      let categoryId = id;
      
      if (isEdit) {
        await updateCategory(id, saveData);
      } else {
        const createResponse = await createCategory(saveData);
        categoryId = createResponse.data._id;
        
        // Upload images for new category if they exist
        const imageTypes = ['icon', 'image'];
        for (const type of imageTypes) {
          const fileData = formData[`${type}_file`];
          if (fileData) {
            try {
              const response = await uploadCategoryImage(categoryId, {
                attribute: type,
                data: fileData.data,
                name: fileData.name,
                dataType: fileData.dataType
              });
              console.log(`${type} uploaded successfully:`, response.data);
            } catch (uploadError) {
              console.error(`Failed to upload ${type}:`, uploadError);
            }
          }
        }
      }
      
      history.push('/categories');
    } catch (error) {
      console.error("Failed to save category:", error);
    } finally {
      setSaving(false);
    }
  };

  // Tax code search helpers
  const getFilteredTaxCodes = () => {
    if (!taxCodeSearch.trim()) {
      return taxCodes.slice(0, 50); // Show first 50 if no search
    }
    const searchTerm = taxCodeSearch.toLowerCase();
    return taxCodes.filter(code => 
      code.taxCode.toLowerCase().includes(searchTerm) ||
      code.description.toLowerCase().includes(searchTerm)
    ).slice(0, 50); // Limit to 50 results for performance
  };

  const getSelectedTaxCode = () => {
    return taxCodes.find(code => code.taxCode === formData.avatax_code);
  };

  const getTaxCodeDisplayValue = () => {
    const selected = getSelectedTaxCode();
    return selected ? `${selected.taxCode} - ${selected.description}` : formData.avatax_code;
  };

  const handleTaxCodeSearchChange = (event) => {
    setTaxCodeSearch(event.target.value);
  };

  const handleTaxCodeFocus = (event) => {
    setTaxCodeAnchorEl(event.currentTarget);
    setTaxCodeDropdownOpen(true);
    if (!taxCodeSearch) {
      const selected = getSelectedTaxCode();
      setTaxCodeSearch(selected ? `${selected.taxCode} - ${selected.description}` : '');
    }
  };

  const handleTaxCodeSelect = (taxCode) => {
    handleInputChange('avatax_code', taxCode.taxCode);
    setTaxCodeSearch(`${taxCode.taxCode} - ${taxCode.description}`);
    setTaxCodeDropdownOpen(false);
  };

  const handleTaxCodeClickAway = () => {
    setTaxCodeDropdownOpen(false);
    // Reset search to display selected value
    const selected = getSelectedTaxCode();
    setTaxCodeSearch(selected ? `${selected.taxCode} - ${selected.description}` : '');
  };

  const handleImageUpload = async (type, file) => {
    try {
      setUploading(prev => ({ ...prev, [type]: true }));
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
          const fileName = file.name;
          
          if (isEdit) {
            // For existing categories, upload via API
            const response = await uploadCategoryImage(id, {
              attribute: type,
              data: base64Data,
              name: fileName,
              dataType: file.type
            });
            
            // Update form data with the returned URL
            const imageUrl = response.data[type] || response.data;
            handleInputChange(type, imageUrl);
          } else {
            // For new categories, store the base64 data temporarily
            // It will be uploaded after category creation
            handleInputChange(`${type}_file`, {
              data: base64Data,
              name: fileName,
              dataType: file.type
            });
            // Set a temporary preview URL
            handleInputChange(type, reader.result);
          }
        } catch (error) {
          console.error(`Failed to upload ${type}:`, error);
          alert(`Failed to upload ${type}. Please try again.`);
        } finally {
          setUploading(prev => ({ ...prev, [type]: false }));
        }
      };
      
      reader.onerror = () => {
        console.error('Error reading file');
        setUploading(prev => ({ ...prev, [type]: false }));
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error(`Error processing ${type} file:`, error);
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleFileInputChange = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB.');
        return;
      }
      
      handleImageUpload(type, file);
    }
  };

  const filterCategoryTreeBySearch = (category, searchTerm) => {
    const matchesSearch = !searchTerm || category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const filteredChildren = (category.children || [])
      .map(child => filterCategoryTreeBySearch(child, searchTerm))
      .filter(Boolean);
    if (matchesSearch || filteredChildren.length > 0) {
      return { ...category, children: filteredChildren };
    }
    return null;
  };

  const getFilteredParentCategories = () => {
    const term = parentSearch.trim();
    if (!term) return categories;
    return categories.map(c => filterCategoryTreeBySearch(c, term)).filter(Boolean);
  };

  const renderCategoryTree = (categories, depth = 0) => {
    return categories.map(category => (
      <div key={category._id} style={{ paddingLeft: depth * 20 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.parent_id === category._id}
              onChange={() => handleParentChange(category)}
              color="primary"
            />
          }
          label={category.name}
        />
        {category.children && category.children.length > 0 && (
          renderCategoryTree(category.children, depth + 1)
        )}
      </div>
    ));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <Edit />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {isEdit ? `Edit Category: ${formData.name}` : "Create New Category"}
              </h4>
              <div style={{ position: 'absolute', right: '20px', top: '20px', zIndex: 10 }}>
                <Button 
                  color="transparent" 
                  size="sm" 
                  onClick={handleBack}
                  style={{ marginRight: '10px' }}
                >
                  <ArrowBack /> Back
                </Button>
                <Button 
                  color="success" 
                  size="sm" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Save /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <GridContainer spacing={3}>
                {/* Category Details Section */}
                <GridItem xs={12}>
                  <Card>
                    <CardHeader>
                      <h4>Category Details</h4>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.enabled}
                            onChange={(e) => handleInputChange('enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Active"
                        style={{ float: 'right' }}
                      />
                    </CardHeader>
                    <CardBody>
                      <GridContainer spacing={2}>
                        <GridItem xs={12} md={6}>
                          <CustomInput
                            labelText="Title *"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.name,
                              onChange: (e) => handleInputChange('name', e.target.value),
                              placeholder: "How you would like the category to display to customers"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12} md={6}>
                          <CustomInput
                            labelText="Subtitle"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.subtitle,
                              onChange: (e) => handleInputChange('subtitle', e.target.value),
                              placeholder: "Enter a subtitle"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12}>
                          <CustomInput
                            labelText="Description"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              multiline: true,
                              rows: 4,
                              value: formData.html,
                              onChange: (e) => handleInputChange('html', e.target.value),
                              placeholder: "Description text here"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12} md={6}>
                          <CustomInput
                            labelText="URL"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.url_name,
                              onChange: (e) => handleInputChange('url_name', e.target.value),
                              placeholder: "Category URL slug"
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.url_prefix}
                                onChange={(e) => handleInputChange('url_prefix', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Prefix subcategories with url"
                          />
                        </GridItem>
                        <GridItem xs={12} md={6}>
                          <CustomInput
                            labelText="Position"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              type: "number",
                              value: formData.ordinal,
                              onChange: (e) => handleInputChange('ordinal', parseInt(e.target.value) || 0),
                              placeholder: "The category's position in the main menu"
                            }}
                          />
                          <CustomInput
                            labelText="Web Position"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              type: "number",
                              value: formData.web_ordinal,
                              onChange: (e) => handleInputChange('web_ordinal', parseInt(e.target.value) || 0),
                              placeholder: "The category's position in the main menu"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12} md={6}>
                          <CustomInput
                            labelText="Category Tag"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.tag,
                              onChange: (e) => handleInputChange('tag', e.target.value),
                              placeholder: "The category tag for products"
                            }}
                          />
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Parent Category Section */}
                <GridItem xs={12}>
                  <Card>
                    <CardHeader>
                      <h4>Parent Category</h4>
                    </CardHeader>
                    <CardBody>
                      <CustomInput
                        formControlProps={{ fullWidth: true, style: { marginTop: 0, paddingTop: 0, marginBottom: '12px' } }}
                        inputProps={{
                          placeholder: "Search categories...",
                          value: parentSearch,
                          onChange: (e) => setParentSearch(e.target.value),
                          endAdornment: (
                            <InputAdornment position="end">
                              <Search style={{ color: '#aaa' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                        {getFilteredParentCategories().length === 0 ? (
                          <div style={{ color: '#999', fontStyle: 'italic', padding: '8px' }}>
                            No categories found
                          </div>
                        ) : (
                          renderCategoryTree(getFilteredParentCategories())
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Platforms Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>Platforms</h4>
                    </CardHeader>
                    <CardBody>
                      <GridContainer>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.platforms.includes('ios')}
                                onChange={() => handlePlatformToggle('ios')}
                                color="primary"
                              />
                            }
                            label="iOS"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.platforms.includes('android')}
                                onChange={() => handlePlatformToggle('android')}
                                color="primary"
                              />
                            }
                            label="Android"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.platforms.includes('web')}
                                onChange={() => handlePlatformToggle('web')}
                                color="primary"
                              />
                            }
                            label="Web"
                          />
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Navigation Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>Navigation</h4>
                    </CardHeader>
                    <CardBody>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.display_subproducts}
                            onChange={(e) => handleInputChange('display_subproducts', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Show Products"
                      />
                      <br />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.display_subcategories}
                            onChange={(e) => handleInputChange('display_subcategories', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Show Subcategories"
                      />
                    </CardBody>
                  </Card>
                </GridItem>
                {/* Age Restriction Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>Age Restriction</h4>
                    </CardHeader>
                    <CardBody>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(formData.is_alcohol)}
                            onChange={(e) => {
                              handleInputChange('is_alcohol', e.target.checked);
                              if (e.target.checked) handleInputChange('is_tobacco', false);
                            }}
                            color="primary"
                          />
                        }
                        label="Alcohol"
                      />
                      <br />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(formData.is_tobacco)}
                            onChange={(e) => {
                              handleInputChange('is_tobacco', e.target.checked);
                              if (e.target.checked) handleInputChange('is_alcohol', false);
                            }}
                            color="primary"
                          />
                        }
                        label="Tobacco"
                      />
                    </CardBody>
                  </Card>
                </GridItem>
                {/* Display Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>Display</h4>
                    </CardHeader>
                    <CardBody>
                      <GridContainer>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('TOP')}
                                onChange={() => handleRootCategoryDisplayToggle('TOP')}
                                color="primary"
                              />
                            }
                            label="Top Icon"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('MAIN_MOBILE')}
                                onChange={() => handleRootCategoryDisplayToggle('MAIN_MOBILE')}
                                color="primary"
                              />
                            }
                            label="Mobile Vertical Menu"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('WEB_STOREFRONT')}
                                onChange={() => handleRootCategoryDisplayToggle('WEB_STOREFRONT')}
                                color="primary"
                              />
                            }
                            label="Web Storefront"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('WEB_MAIN')}
                                onChange={() => handleRootCategoryDisplayToggle('WEB_MAIN')}
                                color="primary"
                              />
                            }
                            label="Web Top Level Navigation"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('WEB_SUB_HEADER')}
                                onChange={() => handleRootCategoryDisplayToggle('WEB_SUB_HEADER')}
                                color="primary"
                              />
                            }
                            label="Web Navigation Header"
                          />
                        </GridItem>
                        <GridItem xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.root_category_display.includes('WEB_SUB')}
                                onChange={() => handleRootCategoryDisplayToggle('WEB_SUB')}
                                color="primary"
                              />
                            }
                            label="Web Navigation Subheader"
                          />
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Tax Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>Tax</h4>
                    </CardHeader>
                    <CardBody>
                      {taxCodes.length > 100 ? (
                        // Use custom searchable dropdown for large datasets (>100 items)
                        <ClickAwayListener onClickAway={handleTaxCodeClickAway}>
                          <div>
                            <TextField
                              label="Tax Code"
                              placeholder="Search tax codes..."
                              fullWidth
                              margin="dense"
                              value={taxCodeDropdownOpen ? taxCodeSearch : getTaxCodeDisplayValue()}
                              onChange={handleTaxCodeSearchChange}
                              onFocus={handleTaxCodeFocus}
                            />
                            <Popper 
                              open={taxCodeDropdownOpen} 
                              anchorEl={taxCodeAnchorEl}
                              style={{ zIndex: 1300, width: taxCodeAnchorEl?.clientWidth || 'auto' }}
                            >
                              <Paper style={{ maxHeight: 300, overflow: 'auto' }}>
                                {getFilteredTaxCodes().map(code => (
                                  <MenuItem 
                                    key={code.taxCode} 
                                    onClick={() => handleTaxCodeSelect(code)}
                                    selected={formData.avatax_code === code.taxCode}
                                  >
                                    <div>
                                      <strong>{code.taxCode}</strong> - {code.description}
                                    </div>
                                  </MenuItem>
                                ))}
                                {getFilteredTaxCodes().length === 0 && (
                                  <MenuItem disabled>
                                    No tax codes found
                                  </MenuItem>
                                )}
                              </Paper>
                            </Popper>
                          </div>
                        </ClickAwayListener>
                      ) : (
                        // Use regular Select for smaller datasets (≤100 items)
                        <FormControl fullWidth margin="dense">
                          <InputLabel>Tax Code</InputLabel>
                          <Select
                            value={formData.avatax_code}
                            onChange={(e) => handleInputChange('avatax_code', e.target.value)}
                          >
                            {taxCodes.map(code => (
                              <MenuItem key={code.taxCode} value={code.taxCode}>
                                {code.taxCode} - {code.description}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <br /><br />
                      <h6>Redemption Exempt States</h6>
                      <GridContainer>
                        {REDEMPTION_EXEMPT_STATES.map(state => (
                          <GridItem xs={6} sm={4} md={3} key={state}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.redemption_exempt_states[state] || false}
                                  onChange={() => handleRedemptionStateToggle(state)}
                                  color="primary"
                                />
                              }
                              label={state}
                            />
                          </GridItem>
                        ))}
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* SEO Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>SEO</h4>
                    </CardHeader>
                    <CardBody>
                      <GridContainer spacing={2}>
                        <GridItem xs={12}>
                          <CustomInput
                            labelText="Page Title"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.seo_title,
                              onChange: (e) => handleInputChange('seo_title', e.target.value),
                              placeholder: "(Optional)"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12}>
                          <CustomInput
                            labelText="Meta Keywords"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.seo_keywords,
                              onChange: (e) => handleInputChange('seo_keywords', e.target.value),
                              placeholder: "(Optional)"
                            }}
                          />
                        </GridItem>
                        <GridItem xs={12}>
                          <CustomInput
                            labelText="Meta Description"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              value: formData.seo_description,
                              onChange: (e) => handleInputChange('seo_description', e.target.value),
                              placeholder: "(Optional)"
                            }}
                          />
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Geo Restrictions Section */}
                <GridItem xs={12} md={6}>
                  <Card>
                    <CardHeader>
                      <h4>State Restrictions</h4>
                    </CardHeader>
                    <CardBody>
                      <FormControl fullWidth margin="dense">
                        <Select
                          value=""
                          onChange={(e) => handleGeoRestrictionAdd(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            {getAvailableStatesForGeoRestriction().length === 0 
                              ? "All states selected" 
                              : "Select a state to restrict"
                            }
                          </MenuItem>
                          {getAvailableStatesForGeoRestriction().map(state => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <br /><br />
                      <div>
                        <h6>Restricted States:</h6>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                          {formData.mp_georestriction.length === 0 ? (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>No states restricted</div>
                          ) : (
                            formData.mp_georestriction.map(state => (
                              <Chip
                                key={state}
                                label={state}
                                onDelete={() => handleGeoRestrictionRemove(state)}
                                color="secondary"
                                variant="outlined"
                                size="small"
                              />
                            ))
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                {/* Image Section */}
                <GridItem xs={12}>
                  <Card>
                    <CardHeader>
                      <h4>Images</h4>
                    </CardHeader>
                    <CardBody>
                      <GridContainer spacing={3}>
                        <GridItem xs={12} md={6}>
                          <h6>Icon (Mobile)</h6>
                          {formData.icon ? (
                            <div>
                              <img 
                                src={formData.icon} 
                                alt="Category Icon" 
                                style={{ width: '100px', height: '100px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                              <br /><br />
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => {
                                  removeFormField('icon');
                                  removeFormField('icon_file');
                                }}
                                style={{ marginRight: '10px' }}
                              >
                                <Delete /> Remove Icon
                              </Button>
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="icon-file-input"
                                onChange={(e) => handleFileInputChange('icon', e)}
                              />
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => document.getElementById('icon-file-input').click()}
                                disabled={uploading.icon}
                              >
                                <CloudUpload /> {uploading.icon ? 'Uploading...' : 'Replace Icon'}
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <CustomInput
                                labelText="Icon URL"
                                formControlProps={{ fullWidth: true }}
                                inputProps={{
                                  value: formData.icon && !formData.icon.startsWith('data:') ? formData.icon : '',
                                  onChange: (e) => handleInputChange('icon', e.target.value),
                                  placeholder: "Enter icon URL or upload a file"
                                }}
                              />
                              <br />
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="icon-file-input-empty"
                                onChange={(e) => handleFileInputChange('icon', e)}
                              />
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => document.getElementById('icon-file-input-empty').click()}
                                disabled={uploading.icon}
                              >
                                <CloudUpload /> {uploading.icon ? 'Uploading...' : 'Upload Icon'}
                              </Button>
                            </div>
                          )}
                        </GridItem>
                        <GridItem xs={12} md={6}>
                          <h6>Image (Web)</h6>
                          {formData.image ? (
                            <div>
                              <img 
                                src={formData.image} 
                                alt="Category Image" 
                                style={{ width: '100px', height: '100px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                              <br /><br />
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => {
                                  removeFormField('image');
                                  removeFormField('image_file');
                                }}
                                style={{ marginRight: '10px' }}
                              >
                                <Delete /> Remove Image
                              </Button>
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="image-file-input"
                                onChange={(e) => handleFileInputChange('image', e)}
                              />
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => document.getElementById('image-file-input').click()}
                                disabled={uploading.image}
                              >
                                <CloudUpload /> {uploading.image ? 'Uploading...' : 'Replace Image'}
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <CustomInput
                                labelText="Image URL"
                                formControlProps={{ fullWidth: true }}
                                inputProps={{
                                  value: formData.image && !formData.image.startsWith('data:') ? formData.image : '',
                                  onChange: (e) => handleInputChange('image', e.target.value),
                                  placeholder: "Enter image URL or upload a file"
                                }}
                              />
                              <br />
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="image-file-input-empty"
                                onChange={(e) => handleFileInputChange('image', e)}
                              />
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => document.getElementById('image-file-input-empty').click()}
                                disabled={uploading.image}
                              >
                                <CloudUpload /> {uploading.image ? 'Uploading...' : 'Upload Image'}
                              </Button>
                            </div>
                          )}
                        </GridItem>
                      </GridContainer>
                    </CardBody>
                  </Card>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Dialog open={ageRestrictionConfirmOpen} onClose={() => setAgeRestrictionConfirmOpen(false)}>
        <DialogTitle>Confirm Age Restriction Changes</DialogTitle>
        <DialogContent>
          {ageRestrictionChanged() ? (
            <>
              <DialogContentText>
                You are about to change the age restriction settings for this category:
              </DialogContentText>
              <ul style={{ marginTop: '12px' }}>
                {Boolean(formData.is_alcohol) !== originalAgeRestriction.is_alcohol && (
                  <li>
                    <strong>Alcohol:</strong>{' '}
                    {originalAgeRestriction.is_alcohol ? 'Enabled' : 'Disabled'}{' '}
                    &rarr;{' '}
                    {formData.is_alcohol ? 'Enabled' : 'Disabled'}
                  </li>
                )}
                {Boolean(formData.is_tobacco) !== originalAgeRestriction.is_tobacco && (
                  <li>
                    <strong>Tobacco:</strong>{' '}
                    {originalAgeRestriction.is_tobacco ? 'Enabled' : 'Disabled'}{' '}
                    &rarr;{' '}
                    {formData.is_tobacco ? 'Enabled' : 'Disabled'}
                  </li>
                )}
              </ul>
            </>
          ) : (
            <DialogContentText>
              Confirm this category will not have age-restricted products.
            </DialogContentText>
          )}
          <DialogContentText style={{ marginTop: '12px' }}>
            Are you sure you want to save these changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgeRestrictionConfirmOpen(false)} color="transparent">
            Cancel
          </Button>
          <Button onClick={handleSaveConfirmed} color="success">
            Confirm &amp; Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default connect(
  state => ({
    user: state.app.user,
  })
)(withStyles(styles)(CategoryEditPage));
